import { process } from '@progress/kendo-data-query';
import { GridPagerSettings } from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useTimingLogColumns from 'src/assets/static/edi_admin_tools/useTimingLogColumns';
import GridWrapper from 'src/components/GridWrapper';
import useWindowSize from 'src/hooks/useWindowSize';
import { useContextSelector } from 'use-context-selector';
import { EdiAdminContext } from './EdiAdminContext';
import EdiAdminToolbarActions from './EdiAdminToolbarActions';

const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'multiple',
};

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 25, 50, 100],
};

const defaultProps = {
  tableTextId: 'records',
  DataItemKey: 'documentId',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

const TimingLogsView: FC = () => {
  const { t } = useTranslation();
  const columns = useTimingLogColumns();
  const { height } = useWindowSize();

  const timingLogsDataState = useContextSelector(EdiAdminContext, (ctx) => ctx.timingLogsDataState);
  const onTimingLogsDataStateChange = useContextSelector(
    EdiAdminContext,
    (ctx) => ctx.onTimingLogsDataStateChange
  );
  const timingLogsGridData = useContextSelector(EdiAdminContext, (ctx) => ctx.timingLogsGridData);
  const timingLogsLoading = useContextSelector(EdiAdminContext, (ctx) => ctx.timingLogsLoading);
  const onTimingSelectionChange = useContextSelector(
    EdiAdminContext,
    (ctx) => ctx.onTimingSelectionChange
  );
  const onTimingLogsHeaderSelectionChange = useContextSelector(
    EdiAdminContext,
    (ctx) => ctx.onTimingLogsHeaderSelectionChange
  );

  const gridHeight = useMemo(() => height - 160, [height]);
  const timingLogsProcessedData = useMemo(
    () => process(timingLogsGridData, timingLogsDataState),
    [timingLogsGridData, timingLogsDataState]
  );

  return (
    <main className="px-4">
      <GridWrapper
        id="timingLogsTable"
        className="timing-logs-view"
        columns={columns}
        dataSource={timingLogsProcessedData.data}
        total={timingLogsProcessedData.total}
        dataState={timingLogsDataState}
        onDataStateChange={onTimingLogsDataStateChange}
        height={`${gridHeight}px`}
        gridToolbarChildren={<EdiAdminToolbarActions />}
        {...defaultProps}
        loading={timingLogsLoading}
        noRecordText={t('No records to display')}
        tableTextId={t('records')}
        onSelectionChange={onTimingSelectionChange}
        onHeaderSelectionChange={onTimingLogsHeaderSelectionChange}
      />
    </main>
  );
};

export default TimingLogsView;
